import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import SignupPage from "./pages/SignupPage";
import ConfirmPage from "./pages/ConfirmPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import ManageFrequencyPage from "./pages/ManageFrequencyPage";
import PrivacyNoticePage from "./pages/PrivacyNoticePage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLoginPage from "./pages/AdminLoginPage";
import { getAdminMe } from "./lib/api";

// Protected Route component
function ProtectedRoute({ children, isAuthenticated, isLoading }) {
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9F9F6] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#1A2E4A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#525C6A]">Checking authentication...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const result = await getAdminMe();
        if (result.authenticated) {
          setIsAuthenticated(true);
          setAdminUser(result.username);
        }
      } catch (error) {
        // Not authenticated - that's fine
        setIsAuthenticated(false);
        setAdminUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const handleLogin = (username) => {
    setIsAuthenticated(true);
    setAdminUser(username);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminUser(null);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<SignupPage />} />
          <Route path="/confirm" element={<ConfirmPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/manage-frequency" element={<ManageFrequencyPage />} />
          <Route path="/privacy" element={<PrivacyNoticePage />} />
          <Route path="/terms" element={<TermsOfUsePage />} />
          <Route 
            path="/admin/login" 
            element={
              isAuthenticated ? (
                <Navigate to="/admin" replace />
              ) : (
                <AdminLoginPage onLogin={handleLogin} />
              )
            } 
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
                <AdminDashboard onLogout={handleLogout} />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
